<script setup lang="ts">
	import { MzButton, MzInput, MzTitle, MzCheckbox } from '@monizze/monizze-components';
	import { useI18n } from 'vue-i18n';
	import { useUserStore } from '@/stores/user.ts';
	import { computed, onBeforeMount, ref, type Ref } from 'vue';
	import { useRoute, useRouter } from 'vue-router';
	import { useAuthStore } from '@/stores/auth.ts';
	import { Api } from '@/utils/api.ts';
	import { MfaMethod } from '@/types/MfaMethod.ts';
	import { UserResponse } from '@/types/Responses/UserResponse.ts';
	import ExpirationTimer from '@/components/Mfa/ExpirationTimer.vue';
	import ResendTimer from '@/components/Mfa/ResendTimer.vue';
	import VerificationCodeInput from '@/components/Mfa/VerificationCodeInput.vue';
	import IllustrationCard from '@/components/IllustrationCard.vue';
	import { displayPhone } from '@/utils/mfaUtils.ts';

	const { t } = useI18n();
	const userStore = useUserStore();
	const authStore = useAuthStore();
	const validationCode: Ref<string> = ref('');
	const mfaError: Ref<boolean> = ref(false);
	const isSubmitting: Ref<boolean> = ref(false);
	const onLogin: Ref<boolean> = ref(false);
	const expirationRef: Ref<InstanceType<typeof ExpirationTimer> | null> = ref(null);
	const router = useRouter();
	const route = useRoute();
	const isCard = computed(() => userStore.currentMfaMethod === 'card' || route.query.method === 'card');
	const isTrustedDevice: Ref<boolean> = ref(false);
	const isWeakPassword: Ref<boolean> = ref(false);
	const mfaMethod: Ref<string> = ref((route.query.method as string) ?? userStore.currentMfaMethod);
	const internalRedirect: Ref<string | null> = ref(null);
	const scope: Ref<string | null> = ref(null);
	const mfaToken = computed(() => (scope.value ? authStore.partnerToken : undefined));
	const canChoose = computed(() => {
		return (
			(userStore.mfaMethods.filter((i) => i.method !== 'email').length > 1 && !isActivation.value) ||
			(route.query.isLogin === 'true' && JSON.parse(route.query.methods as string).length > 1) ||
			typeof route.query.channel === 'string'
		);
	});
	const isFuzzion: Ref<boolean> = ref(false);
	const isActivation: Ref<boolean> = ref(false);
	const isLogin: Ref<boolean> = ref(false);
	const partnerUserId: Ref<number | null> = ref(null);

	onBeforeMount(async () => {
		if (route.query.isLogin === 'true') {
			isLogin.value = true;
			userStore.requestMfa(
				!!route.query.single,
				parseInt(route.query.mfaId as string),
				mfaToken.value,
				sessionStorage.getItem('loginPayload') ?? undefined,
				typeof route.query.channel === 'string' ? route.query.channel : undefined,
			);

			return;
		}
		if (route.query.isActivation === 'true' && typeof route.query.method === 'string' && typeof route.query.value === 'string') {
			isActivation.value = true;
			authStore
				.handleLoginMfa(
					route.query.method,
					route.query.value,
					undefined,
					typeof route.query.channel === 'string' ? route.query.channel : undefined,
				)
				.then((result) => {
					sessionStorage.setItem('mfaPayload', result.mfaPayload);
				});
			return;
		}
		/**
		 * MFA on login adds payload & method to URL
		 */
		if (typeof route.query.payload === 'string' && typeof route.query.method === 'string') {
			userStore.setRequestedMfa(route.query.payload, route.query.method);
			onLogin.value = true;
		} else if (typeof route.query.mfaId === 'string') {
			if (typeof route.query.redirect === 'string' && typeof route.query.scope === 'string') {
				/**
				 * MFA coming from other platforms
				 */
				if (route.query.scope === 'partner') {
					scope.value = route.query.scope;
					internalRedirect.value = route.query.redirect;
					if (!authStore.partnerToken) {
						router.replace({ name: 'home' });
						return;
					}
					// Kept out of userStore: the partner-token user may not be the user of this browser's own session.
					Api.get('/user', undefined, authStore.partnerToken)
						.then((response) => response.json() as Promise<UserResponse>)
						.then((body) => (partnerUserId.value = body.data.user_id))
						.catch(() => {});
				}
			}
			if (route.query.isFuzzion === 'true') {
				isFuzzion.value = true;
				await authStore.handleFuzzionMfa(parseInt(route.query.mfaId), route.query.value as string);
				return;
			}
			userStore.requestMfa(
				!!route.query.single,
				parseInt(route.query.mfaId),
				mfaToken.value,
				undefined,
				typeof route.query.channel === 'string' ? route.query.channel : undefined,
			);
			mfaMethod.value = route.query.method as string;
		} else {
			await userStore.requestMfa(
				!!route.query.single,
				route.query.mfaId ? parseInt(route.query.mfaId.toString(), 10) : undefined,
				mfaToken.value,
				undefined,
				typeof route.query.channel === 'string' ? route.query.channel : undefined,
			);
			mfaMethod.value = userStore.currentMfaMethod;
			if (route.query.weakPassword) {
				isWeakPassword.value = true;
			}
		}
	});

	const submitMfa = async () => {
		// A MFA UserValidation is single-use: a successful validate() consumes it server-side, so a second
		// in-flight submit of the same payload fails with «userValidation.data.invalid». Lock out re-entry.
		if (isSubmitting.value) {
			return;
		}
		isSubmitting.value = true;
		try {
			await submitMfaInternal();
		} finally {
			isSubmitting.value = false;
		}
	};

	const submitMfaInternal = async () => {
		if (isActivation.value) {
			const storedPayload = sessionStorage.getItem('mfaPayload') as string;
			await authStore.handleConfirmMfa(storedPayload, validationCode.value).then(() => {
				router.replace({
					name: route.query.redirect as string,
					...(route.query.isSuccess === 'true' && { query: { isSuccess: 'true' } }),
				});
			});
			return;
		} else if (isFuzzion.value) {
			await authStore.handleFuzzion(validationCode.value, typeof route.query.redirect === 'string' ? route.query.redirect : undefined);
			return;
		}
		try {
			mfaError.value = false;
			if (await userStore.activateMfa(validationCode.value, isTrustedDevice.value ?? true, undefined, mfaToken.value)) {
				if (internalRedirect.value) {
					window.location.href = internalRedirect.value;
					internalRedirect.value = '';
					return;
				}
				if (mfaMethod.value === 'totpRecovery') {
					let methods: MfaMethod[];
					let redirect: string | null;

					if (route.query.methods) {
						methods = typeof route.query.methods === 'string' ? JSON.parse(route.query.methods) : route.query.methods;
					} else {
						methods = userStore.mfaMethods;
					}
					const totpMethod = methods.find((m) => m.method === 'totp');
					const totpId = totpMethod ? totpMethod.id : null;

					if (typeof route.query.redirect === 'string') {
						redirect = route.query.redirect;
					} else {
						redirect = internalRedirect.value;
					}

					router.push({
						name: 'mfa-totp-recovery',
						query: {
							mfaId: totpId,
							...(redirect && { redirect: redirect }),
							...(typeof route.query.action === 'string' && { action: route.query.action }),
							...(typeof route.query.targetMfaId === 'string' && { targetMfaId: route.query.targetMfaId }),
						},
					});
					return;
				}
				if (isWeakPassword.value) {
					router.push({ name: 'weak-password', query: { verified: 'true' } });
					return;
				}
				if (!!(isLogin.value ? sessionStorage.getItem('loginPayload') : route.query.payload) && typeof route.query.method === 'string') {
					const customerOnboardingUlid = sessionStorage.getItem('customer_onboarding_mfa_ulid');
					if (customerOnboardingUlid) {
						const customerOnboardingRedirectUrl = sessionStorage.getItem('customer_onboarding_mfa_redirect_url');
						sessionStorage.removeItem('customer_onboarding_mfa_ulid');
						sessionStorage.removeItem('customer_onboarding_mfa_redirect_url');

						await Api.post('/customer/link-existing-user', { ulid: customerOnboardingUlid });

						if (customerOnboardingRedirectUrl) {
							sessionStorage.setItem('customer_onboarding_redirect_url', customerOnboardingRedirectUrl);
						}

						await router.push({ name: 'fuzzion-success' });
						return;
					}
					await router.push(useAuthStore().targetUrl);
					return;
				}

				if (typeof route.query.redirect === 'string') {
					const redirect = route.query.redirect;
					const redirectQuery: any = {};

					if (route.query.action) {
						redirectQuery.action = route.query.action;
					}

					if (route.query.targetMfaId) {
						redirectQuery.targetMfaId = route.query.targetMfaId;
					}

					if (/^https?:\/\//.test(redirect)) {
						window.location.href = redirect;
					} else {
						router.replace({
							name: redirect,
							query: Object.keys(redirectQuery).length > 0 ? redirectQuery : undefined,
						});
					}
					return;
				}

				router.go(-2);
				return;
			}
		} catch (error) {
			// 'weak' is thrown by loginByToken when password_status is 1 or 2.
			// The MFA code itself was valid — redirect to password change instead of
			// showing a false "invalid code" error.
			if (error === 'weak') {
				router.push({ name: 'weak-password', query: { verified: 'true' } });
				return;
			}
			mfaError.value = true;
		}
	};

	const resendCode = () => {
		if (isActivation.value) {
			authStore.handleLoginMfa(route.query.method as string, route.query.value as string).then((result) => {
				sessionStorage.setItem('mfaPayload', result.mfaPayload);
			});
		} else if (isFuzzion.value) {
			authStore.handleFuzzionMfa(parseInt(route.query.mfaId as string), route.query.value as string);
		} else {
			userStore.requestMfa(
				!!route.query.single,
				typeof route.query.mfaId === 'string' ? parseInt(route.query.mfaId) : undefined,
				mfaToken.value,
				isLogin.value ? (sessionStorage.getItem('loginPayload') as string) : undefined,
				typeof route.query.channel === 'string' ? route.query.channel : undefined,
			);
		}
		expirationRef.value?.resumeExpiration();
	};

	const validationCodeInput = () => {
		if (mfaError.value) {
			mfaError.value = false;
		}
	};

	const handleMfaMethod = () => {
		if (mfaMethod.value !== 'sms') {
			return mfaMethod.value;
		}
		return route.query.channel || 'sms';
	};
</script>

<template>
	<div class="flex flex-col gap-6">
		<div class="flex flex-col gap-2">
			<mz-title type="h1" :text="t('user.mfa.confirm.enable')" />
			<p>{{ t('uzzer.force_mfa.subtitle') }}</p>
		</div>
		<illustration-card :image="`/Mfa/${handleMfaMethod()}.svg`">
			<mz-title type="h2" :text="t(`user.mfa.setup.${mfaMethod}.confirmation-code.title`)" />
			<p v-if="userStore.user.user_id || partnerUserId || (route.query.isLogin === 'true' && isCard)">
				{{ isCard ? t('user.mfa.confirm.card.request') : t(`user.mfa.confirm.code.request_${handleMfaMethod()}`) }}
				<strong v-if="!isCard">
					{{ displayPhone(route.query.value as string) }}
				</strong>
			</p>
			<p v-else>
				{{ t(`user.mfa.confirm.code.${mfaMethod}`) }}
			</p>
			<expiration-timer ref="expirationRef" />
			<mz-input
				v-if="isCard"
				v-model="validationCode"
				inputmode="text"
				pattern="[a-zA-Z0-9]*"
				type="text"
				:info="mfaError ? t('user.mfa.confirm.card.error') : ''"
				:validated="mfaError ? false : undefined"
				:label="t('user.mfa.confirm.card.label')"
				:placeholder="t('user.mfa.confirm.card.placeholder')"
				@keydown.enter="submitMfa"
				@input="validationCodeInput"
			/>
			<verification-code-input
				v-else
				v-model="validationCode"
				:length="mfaMethod === 'totp' ? 6 : 8"
				:label="t(`user.mfa.confirm.code.label_${mfaMethod}`)"
				:has-error="mfaError"
				:error-message="t('user.mfa.confirm.code.error')"
				@update:model-value="validationCodeInput"
				@submit="submitMfa"
			/>
			<mz-checkbox v-if="isLogin" v-model="isTrustedDevice">{{ t('user.mfa.mark.device.trusted') }}</mz-checkbox>
			<resend-timer
				v-if="!isCard && mfaMethod !== 'totp' && mfaMethod !== 'totpRecovery'"
				:method="mfaMethod"
				:time="30"
				type="text"
				@resend="resendCode"
			/>
			<div class="flex flex-col md:flex-row gap-2">
				<mz-button
					class="w-full md:w-auto"
					:label="t('user.mfa.confirm.card.confirm')"
					width="auto"
					:disabled="isSubmitting"
					@click.prevent="submitMfa"
				/>
				<mz-button
					v-if="canChoose"
					class="w-full md:w-auto"
					:label="t('user.mfa.change_method')"
					variant="minimal"
					width="auto"
					@click.prevent="router.back"
				/>
			</div>
		</illustration-card>
	</div>
</template>
